import React, { JSX, RefObject, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  View,
  useWindowDimensions,
} from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useStyleSheet } from '@src/style/service'
import { HealthPermission } from '@src/screens/Home/components/Permissions/HealthPermission'
import {
  AppStackParamList,
  CommonAuthorizedRouteProps,
  CommonAuthorizedStackParamList,
} from '@src/navigation/types'
import { StyleService } from '@src/style/service'
import {
  getHealthPermissionsModalStateInteractions,
  getPermissionsModalStateInteractions,
} from '../../utils'
import { NotificationPermission } from './NotificationPermission'

export interface PermissionsCarouselScreenProps {
  width: number
  onAllowButtonPressCallback: () => void
}

const {
  shouldShow: shouldShowHealthPermissions,
  markAsShown: markHealthPermissionsAsShown,
} = getHealthPermissionsModalStateInteractions()
const {
  shouldShow: shouldShowNotification,
  markAsShown: markPermissionsAsShown,
} = getPermissionsModalStateInteractions()

// During onboarding a bunch of screens are stacked on top of each other
// PermissionsCarousel expects HealthPermission screen to be the last carousel item
//  to ensure correct navigation to the next onboarding screen
const keyExtractor = (_item: unknown, index: number): string => index.toString()

export const PermissionsCarousel = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const carouselRef = useRef<FlatList>(undefined)
  // The carousel's `data` is memoized with empty deps, so each `FlatList` cell
  // captures `goToNextSlide` from the first render. On real devices the cells
  // are not re-rendered when `goToNextSlide` changes identity, so the captured
  // callback keeps reading the initial `activeSlideIndex = 0`. Tracking the
  // active slide in a ref keeps `goToNextSlide` reading the current index even
  // when invoked from a stale cell — without this, the user gets stuck on the
  // last slide (Apple Health) because `scrollToIndex(1)` is a no-op.
  const activeSlideIndexRef = useRef(0)
  const insets = useSafeAreaInsets()

  const route = useRoute<CommonAuthorizedRouteProps<'PermissionsCarousel'>>()

  const { params } = route

  const initialRenderedRef = useRef(false)
  const healthPermissionRenderedRef = useRef(false)

  const permissionScreens = useMemo(() => {
    const screens = [] as { renderScreen: (width: number, callback: () => void) => JSX.Element }[]
    if (shouldShowNotification()) {
      screens.push({
        renderScreen: (width: number, onAllowButtonPressCallback: () => void) => (
          <NotificationPermission
            width={width}
            onAllowButtonPressCallback={onAllowButtonPressCallback}
          />
        ),
      })
      markPermissionsAsShown()
    }

    if (shouldShowHealthPermissions()) {
      screens.push({
        renderScreen: (width: number, onAllowButtonPressCallback: () => void) => {
          healthPermissionRenderedRef.current = true

          return (
            <HealthPermission
              width={width}
              onAllowButtonPressCallback={onAllowButtonPressCallback}
            />
          )
        },
      })
      markHealthPermissionsAsShown()
    }

    return screens
    // We only want this useEffect to run on component mount, so deps must be empty
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dimensions = useWindowDimensions()
  const itemWidth = dimensions.width

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const index = Math.round(event.nativeEvent.contentOffset.x / itemWidth)
    activeSlideIndexRef.current = index
  }

  const lastSlideIndex = permissionScreens.length - 1

  const nextScreen = params?.nextScreen

  const goToNextSlide = useCallback(() => {
    const currentSlideIndex = activeSlideIndexRef.current
    if (currentSlideIndex < lastSlideIndex) {
      carouselRef.current?.scrollToIndex({ index: currentSlideIndex + 1 })
      return
    }

    if (nextScreen) {
      authorizedNavigation.replace(nextScreen.screen, nextScreen.params)
      return
    }

    if (navigation.canGoBack()) {
      navigation.goBack()
      return
    }

    navigation.replace('Drawer', { screen: 'Dashboard' })
  }, [authorizedNavigation, lastSlideIndex, navigation, nextScreen])

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // react only to subsequent focus events
      if (!initialRenderedRef.current) {
        initialRenderedRef.current = true
        return
      }

      if (healthPermissionRenderedRef.current) {
        goToNextSlide()
      }
    })

    return () => {
      unsubscribe()
    }
  }, [goToNextSlide, navigation])

  return (
    <View style={[styles.contentContainer, { paddingTop: insets.top }]}>
      <FlatList
        ref={carouselRef as RefObject<FlatList>}
        data={permissionScreens}
        renderItem={({ item }: { item: typeof permissionScreens[0] }) =>
          item.renderScreen(itemWidth, goToNextSlide)
        }
        horizontal
        keyExtractor={keyExtractor}
        onScroll={onScroll}
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
      />
    </View>
  )
}

const themedStyle = StyleService.create({
  contentContainer: {
    flexGrow: 1,
    borderRadius: 16,
    backgroundColor: 'theme.background',
  },
})
